import React from 'react';

import { Autocomplete, Box, Button, Checkbox, Chip, MenuItem, Paper, Stack, TextField } from '@mui/material';

import { FILTER_DEFINITIONS, type FilterKey, type ManifestFilters, defaultFilters, lastCompleteDay, periodWindow, type PeriodMode } from './model';

type Props = {
  filters: ManifestFilters;
  options: Record<string, Array<string>>;
  onChange: (change: Partial<ManifestFilters>) => void;
};

/** Option lists stop at 500 values, so longer lists also accept a typed value. */
const OPTION_LIMIT = 500;

export default function LtlpManifestFilters({ filters, options, onChange }: Props) {
  const saved = defaultFilters(filters.reportView);
  const maxDay = lastCompleteDay();
  const select = (key: FilterKey, values: Array<string>) =>
    onChange({ selections: { ...filters.selections, [key]: values } });
  const isSaved =
    filters.periodMode === saved.periodMode &&
    filters.from === saved.from &&
    filters.to === saved.to &&
    FILTER_DEFINITIONS.every(
      ({ key }) =>
        JSON.stringify([...filters.selections[key]].sort()) === JSON.stringify([...saved.selections[key]].sort()),
    );

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.5,
        }}
      >
        <TextField
          select
          label="Transaction date period"
          size="small"
          value={filters.periodMode}
          onChange={(event) => {
            const periodMode = event.target.value as PeriodMode;
            onChange(periodMode === 'custom' ? { periodMode } : { periodMode, ...periodWindow(periodMode) });
          }}
        >
          <MenuItem value="last-week">Previous week</MenuItem>
          <MenuItem value="last-4-weeks">Previous 4 weeks</MenuItem>
          <MenuItem value="last-month">Previous month</MenuItem>
          <MenuItem value="this-year">This year to yesterday</MenuItem>
          <MenuItem value="last-year">Previous year</MenuItem>
          <MenuItem value="custom">Custom dates</MenuItem>
        </TextField>
        {(['from', 'to'] as const).map((key) => (
          <TextField
            key={key}
            label={key === 'from' ? 'Transaction date from' : 'Transaction date to'}
            type="date"
            size="small"
            value={filters[key]}
            onChange={(event) => onChange({ periodMode: 'custom', [key]: event.target.value })}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: maxDay } }}
          />
        ))}
        {FILTER_DEFINITIONS.map(({ key, label }) => {
          const selected = filters.selections[key];
          const listed = options[key] ?? [];
          return (
            <Autocomplete
              key={key}
              multiple
              freeSolo={listed.length >= OPTION_LIMIT}
              disableCloseOnSelect
              size="small"
              limitTags={1}
              options={[...new Set([...listed, ...selected])]}
              value={selected}
              onChange={(_, values) => select(key, values.map(String))}
              renderTags={(values, getTagProps) =>
                values.map((option, index) => {
                  const { key: tagKey, ...tagProps } = getTagProps({ index });
                  return <Chip key={tagKey} {...tagProps} label={option} size="small" />;
                })
              }
              renderOption={(props, option, { selected: checked }) => (
                <Box component="li" {...props} key={option}>
                  <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
                  {option}
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label={label} placeholder={selected.length ? undefined : '(All)'} />
              )}
              sx={{ minWidth: 0, '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 45px)' } }}
            />
          );
        })}
      </Box>
      {!isSaved && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button size="small" onClick={() => onChange(saved)}>
            Reset to saved view
          </Button>
        </Stack>
      )}
    </Paper>
  );
}
